import React, { useEffect, useState, useRef } from 'react';
import classnames from 'classnames';

import { Button } from '@BICE_Vida/components';
import ReactModal from 'react-modal';
import Text from '../Text';

import styles from './InfoModal.module.scss';

type InfoModalPropsT = {
  id?: string;
  isOpen: boolean;
  textTitle?: string;
  textDescription?: string;
  button?: {
    id: string;
    text: string;
    click: React.MouseEventHandler;
    disabled?: boolean;
    loading?: boolean;
  };
  children?: React.ReactNode;
  close?: () => void;
  getRef?: (ref: React.RefObject<HTMLDivElement>, isOpen: boolean) => void;
  className?: string;
  overlayClass?: string;
  closeOnOverlay?: boolean;
  closeOnIcon?: boolean;
  imgSrc?: string;
  imgClassName?: string;
  imgHeight?: number;
  imgWidth?: number;
  titleClassName?: string;
};

function InfoModal({
  id = '',
  isOpen = false,
  textTitle,
  textDescription,
  button,
  children,
  close,
  getRef = () => {},
  className,
  overlayClass,
  closeOnOverlay = false,
  closeOnIcon = true,
  imgSrc,
  imgClassName,
  imgHeight,
  imgWidth,
  titleClassName,
}: InfoModalPropsT) {
  const modalContainerRef = useRef<HTMLDivElement>(null);

  const [overlayClassShow, setOverlayClassShow] = useState(styles.modal_hide);
  const overlayClasses = [styles.modal_overlay];
  const modalClasses = classnames(styles.modal_modal, {
    [styles.with_close_icon]: !!closeOnIcon && !!close,
    [className ?? '']: !!className,
  });

  if (overlayClass) {
    overlayClasses.push(overlayClass);
  }

  const onRequestClose = closeOnOverlay && !!close ? close : undefined;

  useEffect(() => {
    let timeOut: ReturnType<typeof setTimeout>;

    if (isOpen) {
      document.documentElement.style.overflow = 'hidden';

      timeOut = setTimeout(() => {
        setOverlayClassShow(styles.modal_show);
        getRef(modalContainerRef, isOpen);
      }, 50);
    } else {
      document.documentElement.style.overflow = 'auto';

      setOverlayClassShow(styles.modal_hide);
      getRef(modalContainerRef, isOpen);
    }

    return () => {
      clearTimeout(timeOut);
    };
  }, [isOpen]);

  return (
    <ReactModal
      isOpen={isOpen}
      closeTimeoutMS={300}
      className={modalClasses}
      ariaHideApp={false}
      overlayClassName={`${overlayClasses.join(' ')} ${overlayClassShow}`}
      onRequestClose={onRequestClose}
      shouldCloseOnEsc={false}
      shouldCloseOnOverlayClick={closeOnOverlay}
      shouldFocusAfterRender={false}
    >
      {closeOnIcon && !!close && (
        <div className={styles.modal_icon_container} onClick={close}>
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M18 6L6 18M6 6L18 18"
              stroke="#666"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
      )}
      <div ref={modalContainerRef} id={id} className={styles.modal_container}>
        <div className={styles.modal_body}>
          <div className={styles.modal_content}>
            {!!imgSrc && (
              <div className={classnames(styles.modal_img, imgClassName)}>
                <img src={imgSrc} height={imgHeight} width={imgWidth} alt="icono" />
              </div>
            )}
            {!!textTitle && (
              <Text text={textTitle} customClass={classnames(styles.modal_title, titleClassName)} />
            )}
            {!!textDescription && (
              <Text text={textDescription} customClass={styles.modal_description} />
            )}
            <div className={styles.modal_children}>{children}</div>
          </div>
        </div>
        {!!button && (
          <div className={styles.modal_btn_container}>
            <Button
              id={button.id}
              text={button.text}
              onClick={button.click}
              disabled={button.disabled}
              loading={button.loading}
            />
          </div>
        )}
      </div>
    </ReactModal>
  );
}

export default InfoModal;
